import { LogOut, Shield, UserCircle } from "lucide-react";
import { useNavigate } from "react-router";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";
import { useMeDialog } from "@/lib/me-dialog";
import { userAvatarInitial, userDisplayLabel } from "@/lib/user-display";

function UserAvatar({ user, className }: { user: NonNullable<ReturnType<typeof useAuth>["user"]>; className?: string }) {
  const label = userDisplayLabel(user);
  const initial = userAvatarInitial(user);

  return (
    <Avatar className={className}>
      {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={label} /> : null}
      <AvatarFallback className="bg-primary/10 font-medium text-primary">{initial}</AvatarFallback>
    </Avatar>
  );
}

export function NavUser() {
  const { user, logout } = useAuth();
  const { isMobile, setOpenMobile } = useSidebar();
  const { openMe } = useMeDialog();
  const navigate = useNavigate();

  if (!user) return null;

  const label = userDisplayLabel(user);

  function handlePersonalCenter() {
    setOpenMobile(false);
    openMe();
  }

  function handlePlatformAdmin() {
    setOpenMobile(false);
    navigate("/platform");
  }

  async function handleLogout() {
    setOpenMobile(false);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <UserAvatar user={user} className="size-8" />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{label}</span>
                <span className="truncate text-xs text-muted-foreground">ID {user.id}</span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <UserAvatar user={user} className="size-8" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{label}</span>
                  <span className="truncate text-xs font-heading text-muted-foreground">c456 商家中心</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onSelect={handlePersonalCenter}>
              <UserCircle />
              个人中心
            </DropdownMenuItem>
            {user.platform_role ? (
              <DropdownMenuItem className="cursor-pointer" onSelect={handlePlatformAdmin}>
                <Shield />
                平台管理
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem className="cursor-pointer" onSelect={() => void handleLogout()}>
              <LogOut />
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
